import { useCallback } from 'react';
import { PORTAL_PARENT_CLASS } from '../contexts/PortalContext';
import { useEventListener } from './useEventListener';

interface TargetEvent {
  key: KeyboardEvent['key'];
  metaKey?: KeyboardEvent['metaKey'];
  ctrlKey?: KeyboardEvent['ctrlKey'];
  shiftKey?: KeyboardEvent['shiftKey'];
  altKey?: KeyboardEvent['altKey'];
}

/**
 * A protected drawer is a drawer that is protected from being closed by the escape key, unless
 * all modals and drawers are closed.
 * It is used in the OMS Drawer's DetailsDrawer and AppwideDrawer components, which both handle escape closing
 */
export const PROTECTED_DRAWER_CLASSNAME = 'protected-drawer';
/**
 * Check if an element is a protected drawer.
 * @param element - The element to check.
 * @returns True if the element is a protected drawer, false otherwise.
 */
export function isProtectedDrawer(element: HTMLElement) {
  return element.classList.contains(PROTECTED_DRAWER_CLASSNAME);
}

/**
 * Check if there is text selection and if a given element is within the same focus-lock as the selection
 * @param element - Optional element to check if it's in the same focus-lock as the selection
 * @returns True if there's text selection (and optionally if element is in same focus-lock), false otherwise
 */
function hasTextSelectionInFocusLock(element?: HTMLElement): boolean {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return false;
  }

  // Check all ranges in the selection
  for (let i = 0; i < selection.rangeCount; i++) {
    const range = selection.getRangeAt(i);

    // Get the element that contains the selection
    const selectionContainer = range.commonAncestorContainer;
    let selectionElement: Element | null = null;

    if (selectionContainer.nodeType === Node.TEXT_NODE) {
      selectionElement = selectionContainer.parentElement;
    } else if (selectionContainer.nodeType === Node.ELEMENT_NODE) {
      selectionElement = selectionContainer as Element;
    }

    if (selectionElement) {
      // Find the closest focus-lock to the selection
      const selectionFocusLock = selectionElement.closest('.focus-lock');

      if (selectionFocusLock) {
        // If no specific element to check, we found selection in a focus-lock
        if (!element) {
          return true;
        }

        // If element is provided, check if it's in the same focus-lock
        return selectionFocusLock.contains(element);
      }
    }
  }

  return false;
}

/**
 * Check if any modal or focus-lock container has focus or text selection
 * @returns True if any modal/focus-lock has focus, false otherwise
 */
function hasModalFocus(): boolean {
  const activeElement = document.activeElement;

  // Check for dialogs with focus, but skip a protected drawer's own outermost role="dialog" wrapper.
  // Nested dialogs still block parent drawer Escape because they have another dialog ancestor.
  const focusedDialogs = document.querySelectorAll('[role="dialog"]:focus-within');
  for (const dialog of Array.from(focusedDialogs)) {
    const isProtectedDrawerOwnWrapper =
      !!dialog.closest(`.${PROTECTED_DRAWER_CLASSNAME}`) && !dialog.parentElement?.closest('[role="dialog"]');
    if (!isProtectedDrawerOwnWrapper) {
      return true;
    }
  }

  if (activeElement) {
    // Check for react-focus-lock containers with active elements
    const focusLockContainer = activeElement.closest('.focus-lock');
    if (focusLockContainer) {
      return true;
    }

    // Portalized dropdowns: visibility guard skips focus stuck on a hidden input after close.
    if (activeElement.closest(`.${PORTAL_PARENT_CLASS}`) && getComputedStyle(activeElement).visibility !== 'hidden') {
      return true;
    }
  }

  // Check for text selection within any focus-lock container
  return hasTextSelectionInFocusLock();
}

/**
 * Check if a specific element has focus or text selection
 * @param element - The element to check
 * @returns True if the element has focus, false otherwise
 */
function hasElementFocus(element: HTMLElement): boolean {
  const activeElement = document.activeElement;
  const isFocused = element === activeElement || element.contains(activeElement);

  if (isFocused) {
    return true;
  }

  return hasTextSelectionInFocusLock(element);
}

/**
 * A hook for dialogs that listens for a key down event and calls the onKeyDown function if the key matches the targetEvent.
 * - If escape is pressed, it will call the onKeyDown function if the element is focused or a protected drawer.
 * - If the escape key is pressed and the element is not focused or a protected drawer, it will not call the onKeyDown function.
 * - If the escape key is pressed and the element is focused and a protected drawer, it will not call the onKeyDown function.
 * @param targetEvent - The event to listen for.
 * @param onKeyDown - The function to call when the key is pressed.
 * @param ref - The ref to the element to check if it is a protected drawer.
 */
export const useOnKeyDown = (
  targetEvent: TargetEvent,
  onKeyDown?: (e: KeyboardEvent) => void,
  ref?: React.RefObject<HTMLElement>
) => {
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      onKeyDown?.(e);
      e.stopPropagation();
      return;
    },
    [onKeyDown]
  );

  const handleFocusedElementEscape = useCallback(
    (e: KeyboardEvent, isFocused: boolean): boolean => {
      if (isFocused) {
        handleEscape(e);
        return true;
      }
      return false;
    },
    [handleEscape]
  );

  const handleProtectedDrawerEscape = useCallback(
    (e: KeyboardEvent, element: HTMLElement): boolean => {
      if (isProtectedDrawer(element)) {
        const modalHasFocus = hasModalFocus();

        e.stopPropagation();
        if (!modalHasFocus) {
          handleEscape(e);
        }
        return true;
      }
      return false;
    },
    [handleEscape]
  );

  const handler = useCallback(
    (e: KeyboardEvent) => {
      if (!onKeyDown) {
        return;
      }

      for (const property in targetEvent) {
        // @ts-expect-error - Valid check for optional properties
        if (e[property] !== targetEvent[property]) {
          return;
        }
      }

      // For escape key events, check if this is a protected drawer and if any modal has focus
      if (targetEvent.key === 'Escape' && ref?.current) {
        if (isProtectedDrawer(ref.current)) {
          const handled = handleProtectedDrawerEscape(e, ref.current);
          if (handled) {
            return;
          }
        }

        const isElementFocused = hasElementFocus(ref.current);

        const handled = handleFocusedElementEscape(e, isElementFocused);
        if (handled) {
          return;
        }

        // If we get here, we didn't process the escape key, so we need to call the onKeyDown function
        return;
      }

      onKeyDown?.(e);
      e.preventDefault();
    },
    [handleFocusedElementEscape, handleProtectedDrawerEscape, onKeyDown, ref, targetEvent]
  );

  useEventListener('keydown', handler);
};
